import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_get_prep_plan, API_post_prep_count, API_post_prep_results } from "../../../constants";
import { getDefaultAxiosConfig } from "../../../utilities/requests";

const MANAGER_ROLES = ["rightwork_internal", "company_admin", "company_manager"];
// `checked` turns true once this location has an answer (available or not).
const empty = { available: false, checked: false, plan: null, loading: false, error: null };

// Prep plan for the selected location. While the tab isn't open this only
// probes availability (does the company have prep items?). Availability is
// kept while a new location loads so the tab doesn't flicker away mid-switch.
export default function usePrepPlan(storeId, permissionRole, active, date) {
  const allowed = MANAGER_ROLES.includes(permissionRole) && Boolean(storeId);
  const key = `${storeId}:${permissionRole}`;
  const [state, setState] = useState(empty);
  const requestRef = useRef({ sequence: 0, cancel: null });
  // What the screen is showing right now; a save that finishes after the
  // location or day changed must not put the old plan back.
  const context = `${key}:${date || ""}`;
  const contextRef = useRef(context);
  contextRef.current = context;

  const load = useCallback(async () => {
    requestRef.current.cancel?.();
    const sequence = ++requestRef.current.sequence;
    if (!allowed) {
      setState({ ...empty, key, checked: true });
      return;
    }
    const source = axios.CancelToken.source();
    requestRef.current.cancel = source.cancel;
    setState(previous => ({
      ...(previous.key === key ? previous : { ...empty, available: previous.available }),
      key, loading: true, error: null,
    }));
    try {
      const params = { store_id: storeId, availability_only: active ? "0" : "1" };
      if (active && date) params.date = date;
      const response = await axios.get(API_get_prep_plan, {
        ...getDefaultAxiosConfig(), params, cancelToken: source.token, timeout: 60000,
      });
      if (sequence !== requestRef.current.sequence) return;
      const payload = response.data?.redux?.payload;
      if (typeof payload?.available !== "boolean") throw new Error("Invalid prep plan response");
      setState(previous => ({
        ...empty, key, checked: true, available: payload.available,
        plan: payload.available && Array.isArray(payload.items) ? payload
          : payload.available && previous.key === key ? previous.plan : null,
      }));
    } catch (error) {
      if (axios.isCancel(error) || sequence !== requestRef.current.sequence) return;
      const denied = [401, 403].includes(error.response?.status);
      setState(previous => ({
        ...(denied || previous.key !== key ? empty : previous), key, loading: false, checked: true,
        error: denied ? null : "Couldn’t load the prep plan. Try again in a moment.",
      }));
    }
  }, [allowed, storeId, key, active, date]);

  useEffect(() => {
    const request = requestRef.current;
    load();
    return () => {
      request.cancel?.();
      request.sequence += 1;
    };
  }, [load]);

  // A failed background probe must not hide Prep plan until a page refresh:
  // retry it, as useToastInventory does.
  useEffect(() => {
    if (!allowed || active || state.key !== key || !state.error) return;
    const timer = setTimeout(load, 60000);
    return () => clearTimeout(timer);
  }, [allowed, active, state.key, state.error, key, load]);

  // Entries are often saved back to back (tabbing between boxes). Only the
  // latest save's response is shown; if saves overlapped, the plan is reloaded
  // once they all finish, since an earlier response may predate a later write.
  const saveRef = useRef({ sequence: 0, inFlight: 0, overlapped: false });

  const save = useCallback(async (url, body, failureMessage) => {
    const savedFor = contextRef.current;
    const saves = saveRef.current;
    const saveSequence = ++saves.sequence;
    saves.inFlight += 1;
    if (saves.inFlight > 1) saves.overlapped = true;
    try {
      const response = await axios.post(url, { store_id: storeId, ...body }, getDefaultAxiosConfig());
      const payload = response.data?.redux?.payload;
      if (!Array.isArray(payload?.items)) throw new Error("Invalid prep plan response");
      // Saved either way; only the current view's latest save may redraw it.
      if (contextRef.current === savedFor && saveSequence === saves.sequence) {
        // Drop any in-flight load so an older read can't overwrite the saved entry.
        requestRef.current.cancel?.();
        requestRef.current.sequence += 1;
        setState({ ...empty, key, checked: true, available: payload.available, plan: payload });
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.system?.message || failureMessage, { autoClose: 3000 });
      return false;
    } finally {
      saves.inFlight -= 1;
      if (saves.inFlight === 0 && saves.overlapped) {
        saves.overlapped = false;
        if (contextRef.current === savedFor) load();
      }
    }
  }, [storeId, key, load]);

  const saveCount = useCallback((prepItemUuid, forDate, quantity) => save(
    API_post_prep_count, { prep_item_uuid: prepItemUuid, for_date: forDate, quantity }, "Couldn’t save the count. Try again.",
  ), [save]);

  // `results` holds only the changed fields of each changed item.
  const saveResults = useCallback((forDate, planDate, results) => save(
    API_post_prep_results, { for_date: forDate, plan_date: planDate, results }, "Couldn’t save that day. Try again.",
  ), [save]);

  const current = state.key === key ? state : { ...empty, available: allowed && state.available };
  return { ...current, reload: load, saveCount, saveResults };
}
